/**
 * OrdersTable Component
 * Displays order search results in a structured table format
 * Features: sortable columns, status badges, action buttons, loading states
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Eye,
  Edit,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import type { OrderSearchResultItem } from '../types/filter.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// TYPES
// ============================================

export interface OrdersTableProps {
  orders: OrderSearchResultItem[];
  isLoading: boolean;
  onViewOrder?: (order: OrderSearchResultItem) => void;
  onEditOrder?: (order: OrderSearchResultItem) => void;
  onDeleteOrder?: (orderId: number) => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

// ============================================
// STATUS BADGE CONFIGURATION
// ============================================

const STATUS_STYLES: Record<
  OrderStatus,
  { bg: string; text: string; label: string }
> = {
  PENDING: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Pending',
  },
  CONFIRMED: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    label: 'Confirmed',
  },
  IN_PROGRESS: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    label: 'Processing',
  },
  DELIVERED: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    label: 'Delivered',
  },
  COMPLETED: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    label: 'Completed',
  },
  CANCELLED: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Cancelled',
  },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format amount in Indian currency format
 */
function formatCurrency(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format date in DD/MM/YYYY format
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Get status badge component
 */
function getStatusBadge(status: OrderStatus): React.ReactNode {
  const style = STATUS_STYLES[status];
  return (
    <Badge className={`${style.bg} ${style.text} border-0`}>
      {style.label}
    </Badge>
  );
}

/**
 * Get customer type badge
 */
function getCustomerTypeBadge(customerType: string): React.ReactNode {
  const isB2B = customerType === 'B2B';
  return (
    <Badge
      variant={isB2B ? 'default' : 'secondary'}
      className={isB2B ? 'bg-indigo-100 text-indigo-800' : ''}
    >
      {customerType}
    </Badge>
  );
}

// ============================================
// COMPONENT
// ============================================

export function OrdersTable({
  orders,
  isLoading,
  onViewOrder,
  onEditOrder,
  onDeleteOrder,
  sortField,
  sortDirection,
  onSort,
}: OrdersTableProps): JSX.Element {
  /**
   * Render sort icon for column header
   */
  const renderSortIcon = (field: string) => {
    if (!onSort) return null;

    if (sortField === field) {
      return sortDirection === 'asc' ? (
        <ArrowUp className="ml-2 h-4 w-4 inline" />
      ) : (
        <ArrowDown className="ml-2 h-4 w-4 inline" />
      );
    }

    return <ArrowUpDown className="ml-2 h-4 w-4 inline opacity-50" />;
  };

  /**
   * Handle column header click for sorting
   */
  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Order Date</TableHead>
              <TableHead>Delivery Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-6 w-12 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <div className="rounded-md border border-dashed">
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="rounded-full bg-gray-100 p-4 mb-4">
            <svg
              className="h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            No orders found
          </h3>
          <p className="text-sm text-gray-500 max-w-sm">
            No orders match your current filters. Try adjusting your search
            criteria or clear all filters to see more results.
          </p>
        </div>
      </div>
    );
  }

  // Data table
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className={onSort ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => handleSort('orderNumber')}
            >
              Order #
              {renderSortIcon('orderNumber')}
            </TableHead>
            <TableHead
              className={onSort ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => handleSort('orderStatus')}
            >
              Status
              {renderSortIcon('orderStatus')}
            </TableHead>
            <TableHead
              className={onSort ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => handleSort('totalAmount')}
            >
              Amount
              {renderSortIcon('totalAmount')}
            </TableHead>
            <TableHead
              className={onSort ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => handleSort('orderDate')}
            >
              Order Date
              {renderSortIcon('orderDate')}
            </TableHead>
            <TableHead
              className={onSort ? 'cursor-pointer hover:bg-gray-50' : ''}
              onClick={() => handleSort('expectedDeliveryDate')}
            >
              Delivery Date
              {renderSortIcon('expectedDeliveryDate')}
            </TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Location</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id} className="hover:bg-gray-50">
              <TableCell className="font-medium">
                {order.orderNumber}
              </TableCell>
              <TableCell>{getStatusBadge(order.orderStatus)}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {formatCurrency(order.totalAmount)}
                  </span>
                  {order.balanceDue > 0 && (
                    <span className="text-xs text-red-600">
                      Due: {formatCurrency(order.balanceDue)}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-gray-700">
                {formatDate(order.orderDate)}
              </TableCell>
              <TableCell className="text-gray-700">
                {formatDate(order.expectedDeliveryDate)}
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{order.customerName}</span>
                  <span className="text-xs text-gray-500">
                    {order.customerEmail}
                  </span>
                </div>
              </TableCell>
              <TableCell>{getCustomerTypeBadge(order.customerType)}</TableCell>
              <TableCell className="text-gray-700">
                <div className="flex flex-col">
                  <span>{order.city}</span>
                  <span className="text-xs text-gray-500">{order.state}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {onViewOrder && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewOrder(order)}
                      aria-label={`View order ${order.orderNumber}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  {onEditOrder && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditOrder(order)}
                      aria-label={`Edit order ${order.orderNumber}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onDeleteOrder && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDeleteOrder(order.id)}
                      aria-label={`Delete order ${order.orderNumber}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
